/* @jsxImportSource @emotion/react */
import PropTypes from 'prop-types';

import { useEffect, useReducer, useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import TagManager from 'react-gtm-module';

import SiteNavigation from './components/SiteNavigation';
import LegacySiteNavigation from './components/LegacySiteNavigation';

import Routes from './modules/Routes';
import Button from 'app/common/arctechv2/components/Button';
import { colors } from 'app/common/arctechv2/theme';
import SiteNavLinkBuilder from 'app/common/SiteNavLinkBuilder';
import formatDate from 'date-fns/format';

import { userHasMultipleClients } from 'app/util/hasAccess';

import createPersistedState from 'use-persisted-state';

import { css } from '@emotion/react';

import { clientListRedux } from 'app/resources/reduxStores/clientList';
import { stateListRedux } from 'app/resources/reduxStores/stateList';
import {
    routingGroupListRedux,
    useRoutingGroupList,
} from 'app/resources/reduxStores/routingGroupList';
import { countryListRedux } from 'app/resources/reduxStores/countryList';
import { zoneListRedux } from 'app/resources/reduxStores/zoneList';
import { depotListRedux } from 'app/resources/reduxStores/depotList';

import useExpandedMenu from 'app/common/useExpandedMenu';
import StyledModal from 'app/common/arctechv2/components/StyledModal';
import { eventName as openImageModalEventName } from 'app/events/openImageModalEvent';

import {
    scCss,
    pageMainCss,
    iframeCss,
    publicCss,
} from './SiteContainer.styles';

let RouteX = null;

const SiteContainer = (props) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [, forceUpdate] = useReducer((x) => x + 1, 0);

    const [expandedMenu] = useExpandedMenu();

    const location = useLocation();

    const routingGroupsR = useRoutingGroupList();

    const [showImageModal, setShowImageModal] = useState(false);

    const useBetaNav = createPersistedState('beta-nav');

    const useSiteNavOpen = createPersistedState('site-nav-open');

    const usePageView = createPersistedState('page-view-style');

    const linkBuilder = useMemo(() => {
        return new SiteNavLinkBuilder(
            props.auth.user.role ?? '',
            null,
            formatDate(new Date(), 'yyyy-MM-dd'),
            userHasMultipleClients(props.auth.user)
        );
    }, [props.auth.user]);

    useEffect(() => {
        dispatch(
            clientListRedux.actionCreators.list(props.auth, {
                includeAll: true,
                inline: 'configuration,integrationList',
                limit: -1,
                sort: 'name',
            })
        );
        dispatch(
            countryListRedux.actionCreators.list(props.auth, {
                limit: -1,
                status: 'active',
            })
        );
        dispatch(
            stateListRedux.actionCreators.list(props.auth, {
                limit: -1,
                sort: 'name',
            })
        );
        dispatch(
            routingGroupListRedux.actionCreators.list(props.auth, {
                limit: -1,
                sort: 'name',
                countryId: 1,
            })
        );
        dispatch(
            zoneListRedux.actionCreators.list(props.auth, {
                limit: -1,
                sort: 'name',
            })
        );

        dispatch(
            depotListRedux.actionCreators.list(props.auth, {
                limit: -1,
            })
        );

        window.addEventListener(openImageModalEventName, (event) => {
            setShowImageModal({ url: event.detail.url, tag: event.detail.tag });
        });

        return () => {
            window.removeEventListener(openImageModalEventName, null);
        };
    }, [dispatch, props.auth]);

    const isSpecialRoute =
        props.location.pathname.startsWith('/special') ||
        props.location.pathname.includes('special-route');

    const renderSiteNav =
        isSpecialRoute || props.location.pathname.startsWith('/twilio-flex')
            ? false
            : true;

    let shouldUseBetaNav = false;
    let shouldUsePageView = 'modal';
    let shouldSiteNavOpen = true;

    if (!renderSiteNav) {
        shouldSiteNavOpen = false;
    }

    const [betaNav, setUseBetaNav] = useBetaNav(shouldUseBetaNav);
    const [siteNavOpen, setSiteNavOpen] = useSiteNavOpen(shouldSiteNavOpen);
    const [pageView, setPageView] = usePageView(shouldUsePageView);

    if (betaNav === true && props.auth?.user?.role === 'contractor') {
        setUseBetaNav(false);
        setPageView(pageView);
    }

    if (RouteX == null) {
        if (process.env.NODE_ENV === 'development') {
            import('./modules/DevRoutes').then((y) => {
                RouteX = y.default;
                forceUpdate();
            });
        } else {
            RouteX = Routes;
        }
    }

    let modalView = 'modal';
    if (siteNavOpen && betaNav === false) {
        modalView = 'modal site-nav';
    }

    const tagManagerArgs = {
        dataLayer: {
            userId: props.auth.user.id,
            newMenu: expandedMenu?.enabled,
        },
        dataLayerName: 'PageDataLayer',
    };

    if (process.env.NODE_ENV === 'production') {
        TagManager.dataLayer(tagManagerArgs);
    }

    useEffect(() => {
        const curTitle = linkBuilder
            .getUriTitleMap()
            .find((item) => location.pathname.match(item.path));

        let baseTitle = 'ArcTech';
        if (process.env.NODE_ENV === 'development') {
            baseTitle = 'ArcTech (Dev)';
        }

        if (curTitle && curTitle.title) {
            let args = curTitle.path.exec(location.pathname);
            let titlePrefix = '';

            // 2023-07-25 | Run Summary | Archtech
            // VIC | 2023-07-25 | Run Summary | Archtech
            // HDS 3202 | VIC | 2023-07-25 | Run Summary | Archtech
            if (curTitle.title === 'Run Summary') {
                let params = {
                    countryId: args[1],
                    date: args[2],
                    rtw: args[3],
                    rg: args[4],
                    rz: args[5],
                    z: args[6],
                    c: args[7],
                    t: args[8],
                };

                if (params.date) {
                    titlePrefix = params.date;
                }
                if (params.rg !== 'all') {
                    let rg = routingGroupsR.items?.find(
                        (x) => x.id == params.rg
                    )?.name;
                    titlePrefix = rg + ' | ' + titlePrefix;
                }
            }

            if (titlePrefix !== '') {
                document.title =
                    titlePrefix + ' | ' + curTitle.title + ' | ' + baseTitle;
            } else {
                document.title = curTitle.title + ' | ' + baseTitle;
            }
        } else {
            document.title = baseTitle;
        }
    }, [linkBuilder, location, routingGroupsR.items]);

    return (
        <div id="site-container" css={renderSiteNav ? scCss : null}>
            {props.showNav && !betaNav && renderSiteNav && (
                <LegacySiteNavigation
                    auth={props.auth}
                    location={props.location}
                    siteNavOpen={siteNavOpen}
                    setSiteNavOpen={setSiteNavOpen}
                />
            )}
            {props.showNav && betaNav && renderSiteNav && (
                <SiteNavigation auth={props.auth} location={props.location} />
            )}
            <main
                id="page-main"
                css={
                    !props.showNav
                        ? iframeCss
                        : renderSiteNav
                          ? publicCss
                          : pageMainCss
                }
            >
                <div className={pageView !== 'dual' ? modalView : 'pages'}>
                    {RouteX && <RouteX auth={props.auth} navigate={navigate} />}
                </div>

                <StyledModal
                    showing={showImageModal}
                    setShowing={setShowImageModal}
                    maxWidth={'80vw'}
                    modalCss={css`
                        text-align: center;
                        padding: 1rem !important;
                        width: auto !important;

                        .modal-body,
                        .modal-footer {
                            margin: 0 !important;
                        }
                        .modal-footer,
                        .modal-close {
                            display: none;
                        }

                        img {
                            max-width: 100%;
                            max-height: 80vh;
                            border-radius: 8px;
                        }
                        .btn-image-tag {
                            position: absolute;
                            top: 0.5rem;
                            left: 0.5rem;
                        }
                    `}
                    body={
                        <div>
                            {showImageModal.tag && (
                                <Button
                                    className="btn-image-tag pointer-events-none fw-bold"
                                    color={colors.gray050}
                                    textColor={colors.gray500}
                                >
                                    {showImageModal.tag}
                                </Button>
                            )}
                            <img src={showImageModal.url} />
                        </div>
                    }
                    onHide={() => {
                        setShowImageModal(false);
                    }}
                />
            </main>
        </div>
    );
};
SiteContainer.propTypes = {
    auth: PropTypes.shape({
        user: PropTypes.shape({
            role: PropTypes.string,
            id: PropTypes.number,
        }),
    }),
    location: PropTypes.shape({ pathname: PropTypes.string }),
    showNav: PropTypes.bool,
};

export default SiteContainer;
